/**
 * Resize and compress a single image file using Canvas API.
 * @param {File} file - The image file
 * @param {object} options - { maxW: max width in px, quality: JPEG quality 0-1 }
 * @returns {Promise<File>} - The optimized file (or original if not an image / already small)
 */
export async function optimizeImage(file, { maxW = 800, quality = 0.8 } = {}) {
  if (!file.type.startsWith('image/')) return file;

  const img = await new Promise((resolve) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.src = URL.createObjectURL(file);
  });

  const scale = Math.min(1, maxW / img.naturalWidth);

  if (scale >= 1) {
    URL.revokeObjectURL(img.src);
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  URL.revokeObjectURL(img.src);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality)
  );
  return new File(
    [blob],
    file.name.replace(/\.(png|jpg|jpeg|webp|gif)$/i, '.jpg'),
    { type: 'image/jpeg' }
  );
}
